import { useEffect, useState } from "react";
import type { FolderDTO, ImageDTO } from "@shared/types";
import { api, ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import { Modal } from "./Modal";
import { Button } from "./Button";

interface Props {
  image: ImageDTO | null;
  folders: FolderDTO[];
  onClose: () => void;
  onDone: (updated: ImageDTO) => void;
}

export function MoveDialog({ image, folders, onClose, onDone }: Props) {
  const toast = useToast();
  const [target, setTarget] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (image) {
      setTarget(image.folderId ?? "");
      setError(null);
    }
  }, [image]);

  const sorted = [...folders].sort((a, b) => a.path.localeCompare(b.path));

  const submit = async () => {
    if (!image) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.moveImage(image.id, target || null);
      toast.success("Moved. The direct URL has changed.");
      onDone(res.image);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not move.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!image}
      onClose={busy ? () => {} : onClose}
      title="Move image"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            Move
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-zinc-700">Destination folder</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="field select-chevron cursor-pointer">
            <option value="">Uncategorized (root)</option>
            {sorted.map((f) => (
              <option key={f.id} value={f.id}>
                {f.path}
              </option>
            ))}
          </select>
        </label>
        <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-800">
          The direct URL changes when you move an image.
        </p>
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-[13px] text-red-700">{error}</p>
        )}
      </div>
    </Modal>
  );
}
